import cx from 'classnames';
import CallActiveButtons from 'Components/CallActiveButtons';
import CallButtons from 'Components/CallButtons';
import { CallConnectionState } from 'Components/CallConnectionStatus/index.types';
import CallContactInfo from 'Components/CallContactInfo';
import { RoutedMergeAttendeeOptionsDropdown } from 'Components/MergeAttendeeOptionsDropdown';
import { UserAvatar } from 'Components/shared/UserAvatar';
import { TopBarDialpad } from 'Components/TopBarDialpad';
import { isEmpty, uniqBy } from 'lodash';
import { MobXProviderContext, observer } from 'mobx-react';
import { ContactModelBase } from 'Models/ContactModel';
import { ContactModelV2 } from 'Models/ContactModelV2';
import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader, Popup } from 'semantic-ui-react';
import { VoipMonitorService } from 'Services/voipMonitorService';
import { VoipMonitorEvent } from 'Services/voipMonitorService/index.types';
import { RootStoreProps } from 'Stores/RootStore.types';
import {
  formatNumberWithNationalCode,
  isPhoneNumberMatch,
} from 'Utils/phoneUtil';

import keypad0 from '~/assets/audio/keypad/0.wav';
import keypad1 from '~/assets/audio/keypad/1.wav';
import keypad2 from '~/assets/audio/keypad/2.wav';
import keypad3 from '~/assets/audio/keypad/3.wav';
import keypad4 from '~/assets/audio/keypad/4.wav';
import keypad5 from '~/assets/audio/keypad/5.wav';
import keypad6 from '~/assets/audio/keypad/6.wav';
import keypad7 from '~/assets/audio/keypad/7.wav';
import keypad8 from '~/assets/audio/keypad/8.wav';
import keypad9 from '~/assets/audio/keypad/9.wav';
import keypadAsterisk from '~/assets/audio/keypad/asterisk.wav';
import keypadPound from '~/assets/audio/keypad/pound.wav';
import ellipsisIcon from '~/assets/images/ellipse.svg';
import infoBtn from '~/assets/images/info-btn.svg';
import unknownCallerAvatar from '~/assets/images/unknown-caller-avatar.png';
import { CallConnectionStatus } from '../CallConnectionStatus';
import { CallActiveProps } from './interfaces';

const CallActive = (props: CallActiveProps) => {
  const { phoneCall: phoneStore } =
    React.useContext<RootStoreProps>(MobXProviderContext);

  const [firstPersonFullName, setFirstPersonFullName] = useState<
    string | undefined
  >(undefined);
  const [confAvatarEls, setConfAvatarEls] = useState<
    {
      uniqId: string;
      data: React.ReactNode;
    }[]
  >([]);
  const [showPeopleInRoom, setShowPeopleInRoom] = useState(false);
  const [listOfPeopleInARoom, setListOfPeopleInARoom] = useState<
    {
      uniqId: string;
      data: React.ReactNode;
    }[]
  >([]);
  const [connectionQuality, setConnectionQuality] =
    useState<CallConnectionState>('stable');
  const [avatarElement, setAvatarElement] = useState<React.ReactNode>(null);
  const [numberOfPeopleInConferenceRoom, setNumberOfPeopleInConferenceRoom] =
    useState(0);

  const voipMonitorService = useRef(VoipMonitorService.getInstance());

  const handleConnectionQualityChange = useCallback(
    (quality: CallConnectionState) => {
      setConnectionQuality(quality);
    },
    []
  );

  const uniqueInfoOfPeopleInRoom = useCallback(
    (respData: ContactModelV2 | ContactModelBase) => {
      if (respData instanceof ContactModelV2 && respData.source === 'Bhive') {
        setListOfPeopleInARoom((prev) => [
          ...prev,
          {
            uniqId: respData.name,
            data: (
              <div className={'participant'}>
                <img src={respData.DisplayAvatar} className="avatar" />
                <div className="main">
                  <div className="name">{respData.name}</div>
                  <div className="phone">{respData?.formatNumber()}</div>
                </div>
                <RoutedMergeAttendeeOptionsDropdown
                  markup={
                    <div className="attendee-options-wrapper">
                      <img src={ellipsisIcon} />
                    </div>
                  }
                  sendMessage={props.loadOrCreateConversationWithPost}
                  attendeeId={respData.sourceId}
                  isPerson={true}
                />
              </div>
            ),
          },
        ]);
      } else {
        const isBaseContact = respData instanceof ContactModelBase;

        setListOfPeopleInARoom((prev) => [
          ...prev,
          {
            uniqId: isBaseContact
              ? respData.FormattedPhone
              : ContactModelV2.formatNumber(respData.phoneNumber),
            data: (
              <div className={'participant'}>
                <img
                  src={
                    respData.DisplayAvatar
                      ? respData.DisplayAvatar
                      : unknownCallerAvatar
                  }
                  className="avatar"
                />
                <div className="main">
                  <div className="name">
                    {isBaseContact
                      ? respData.DisplayName
                      : respData
                      ? respData.name
                      : 'Unknown Caller'}
                  </div>
                  <div className="phone">
                    {isBaseContact
                      ? respData.FormattedPhone
                      : ContactModelV2.formatNumber(respData.phoneNumber)}
                  </div>
                </div>
                <RoutedMergeAttendeeOptionsDropdown
                  markup={
                    <div className="attendee-options-wrapper">
                      <img src={ellipsisIcon} />
                    </div>
                  }
                  sendMessage={props.loadOrCreateConversationWithPost}
                  attendeeId={respData.phoneNumber}
                  isPerson={false}
                />
              </div>
            ),
          },
        ]);
      }
    },
    [props.loadOrCreateConversationWithPost]
  );

  const keyPadPush = useCallback(
    (num: string) => {
      phoneStore.ActivePhoneCall.keysPress(num);
      phoneStore.ActivePhoneCall.dialPadNumDtmf(num);
    },
    [phoneStore.ActivePhoneCall]
  );

  const muteCall = useCallback(() => {
    if (!phoneStore.ActivePhoneCall.isCallMuted) {
      phoneStore.ActivePhoneCall.mute();
      phoneStore.ActivePhoneCall.muteIconState('microphone slash');
    } else {
      phoneStore.ActivePhoneCall.unMute();
      phoneStore.ActivePhoneCall.muteIconState('microphone');
    }
  }, [phoneStore.ActivePhoneCall]);

  const pickup = useCallback(() => {
    void phoneStore.ActivePhoneCall.pickup();
  }, [phoneStore.ActivePhoneCall]);

  const reject = useCallback(() => {
    void phoneStore.ActivePhoneCall.reject();
  }, [phoneStore.ActivePhoneCall]);

  const cancel = useCallback(() => {
    if (!phoneStore.isWebRTCConnected || !props.isOnline) {
      phoneStore.cancelCallOnNoWebRTC();
    } else {
      phoneStore.ActivePhoneCall.cancel();
    }
  }, [phoneStore, props.isOnline]);

  const holdCall = useCallback(() => {
    if (!phoneStore.ActivePhoneCall.isCallOnHold) {
      void phoneStore.ActivePhoneCall.hold();
      phoneStore.ActivePhoneCall.onHoldIconState('pause circle');
    } else {
      void phoneStore.ActivePhoneCall.unHold();
      phoneStore.ActivePhoneCall.onHoldIconState('pause circle outline');
    }
  }, [phoneStore.ActivePhoneCall]);

  const transferCall = useCallback(() => {
    phoneStore.ActivePhoneCall.setTransferMode(true);
    if (!phoneStore.ActivePhoneCall.isCallOnHold) {
      void phoneStore.ActivePhoneCall.hold();
    }
  }, [phoneStore.ActivePhoneCall]);

  const displayListofPeopleInARoom = useCallback(async () => {
    if (
      phoneStore.conferenceRoomsIdAndNumbers.has(phoneStore.ConferenceIdGuid)
    ) {
      const peopleInARoomPromise =
        phoneStore.selectPeopleOrContactsFromConferenceRoom(
          phoneStore.ConferenceIdGuid
        );

      const peopleInARoom = await Promise.allSettled(peopleInARoomPromise);

      setShowPeopleInRoom(true);

      peopleInARoom.forEach((p) => {
        p.status === 'fulfilled' && uniqueInfoOfPeopleInRoom(p.value);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    phoneStore,
    uniqueInfoOfPeopleInRoom,
    phoneStore.conferenceRoomsIdAndNumbers,
  ]);

  const closeListOfPeopleInRoom = useCallback(() => {
    setShowPeopleInRoom(false);
    setListOfPeopleInARoom([]);
  }, []);

  const endCall = useCallback(() => {
    // hide the dialpad if it was open and reset the keys being displayed
    if (phoneStore.ActivePhoneCall.showDialPad) {
      phoneStore.ActivePhoneCall.toggleDialPad();
    }
    if (phoneStore.ActivePhoneCall.isWarmTransfer) {
      phoneStore.ActivePhoneCall.setTransferMode(true);
      phoneStore.ActivePhoneCall.setWarmTransfer(false);
    }
    void phoneStore.ActivePhoneCall.hangUp();
  }, [phoneStore.ActivePhoneCall]);

  const renderKeypadAudioElements = useCallback(() => {
    const keypadTones = [
      { name: 'KeyPressTone0', file: keypad0, refName: 'keyPressTone0' },
      { name: 'KeyPressTone1', file: keypad1, refName: 'keyPressTone1' },
      { name: 'KeyPressTone2', file: keypad2, refName: 'keyPressTone2' },
      { name: 'KeyPressTone3', file: keypad3, refName: 'keyPressTone3' },
      { name: 'KeyPressTone4', file: keypad4, refName: 'keyPressTone4' },
      { name: 'KeyPressTone5', file: keypad5, refName: 'keyPressTone5' },
      { name: 'KeyPressTone6', file: keypad6, refName: 'keyPressTone6' },
      { name: 'KeyPressTone7', file: keypad7, refName: 'keyPressTone7' },
      { name: 'KeyPressTone8', file: keypad8, refName: 'keyPressTone8' },
      { name: 'KeyPressTone9', file: keypad9, refName: 'keyPressTone9' },
      {
        name: 'KeyPressTonePound',
        file: keypadPound,
        refName: 'keyPressTonePound',
      },
      {
        name: 'KeyPressToneStar',
        file: keypadAsterisk,
        refName: 'keyPressToneStar',
      },
    ];

    return keypadTones.map((tone) => (
      <audio
        key={tone.name}
        ref={(elem) =>
          phoneStore.ActivePhoneCall &&
          (phoneStore.ActivePhoneCall[tone.refName] = elem)
        }
        src={tone.file}
        id={`${tone.name}-audio`}
        preload="auto"
      />
    ));
  }, [phoneStore.ActivePhoneCall]);

  useEffect(() => {
    voipMonitorService.current.on(
      VoipMonitorEvent.CONNECTION_QUALITY_CHANGED,
      handleConnectionQualityChange
    );

    return () => {
      voipMonitorService.current.removeListener(
        VoipMonitorEvent.CONNECTION_QUALITY_CHANGED,
        handleConnectionQualityChange
      );
    };
  }, [handleConnectionQualityChange]);

  useEffect(() => {
    if (phoneStore.ActivePhoneCall.person !== null) {
      phoneStore.ActivePhoneCall.person?.case({
        pending: () => setAvatarElement(<Loader indeterminate active />),
        rejected: () => setAvatarElement(<UserAvatar />),
        fulfilled: (response) => {
          const person = response;
          const image = person.avatarUrl;

          if (image?.startsWith('http')) {
            setAvatarElement(
              <img
                src={image}
                className={`${
                  phoneStore.ActivePhoneCall?.isCallConnecting
                    ? 'transit-call-image-border'
                    : ''
                }`}
              />
            );
          } else if (phoneStore.ActivePhoneCall.callerIdName) {
            setAvatarElement(
              <UserAvatar name={phoneStore.ActivePhoneCall.callerIdName} />
            );
          } else {
            setAvatarElement(<UserAvatar name={person.name} />);
          }
        },
      });
    } else if (phoneStore.ActivePhoneCall.contact) {
      setAvatarElement(
        <UserAvatar
          name={phoneStore.ActivePhoneCall.contact.name}
          image={phoneStore.ActivePhoneCall.contact.avatarUrl}
        />
      );
    } else {
      setAvatarElement(<UserAvatar />);
    }
  }, [
    phoneStore.ActivePhoneCall.callerIdName,
    phoneStore.ActivePhoneCall?.isCallConnecting,
    phoneStore.ActivePhoneCall.person,
  ]);

  useEffect(() => {
    if (
      phoneStore.ActivePhoneCall.isMerge &&
      phoneStore.conferenceRoomsIdAndNumbers.has(phoneStore.ConferenceIdGuid)
    ) {
      const peopleInARoom = phoneStore.selectPeopleOrContactsFromConferenceRoom(
        phoneStore.ConferenceIdGuid
      );

      peopleInARoom.map((p) => {
        return p?.then((resp) => {
          if (!resp) return null;

          const content = (
            <div className="active-call-small-avatars-popup-content">
              <div className="name">{resp.name || 'Unknown Caller'}</div>
              <div className="number">
                {ContactModelV2.formatNumber(resp.phoneNumber)}
              </div>
            </div>
          );
          const popup = (
            <Popup
              position="top center"
              content={content}
              trigger={
                <img
                  key={resp.id}
                  src={resp.DisplayAvatar || unknownCallerAvatar}
                  className={'avatar'}
                />
              }
            />
          );

          setFirstPersonFullName((firstPersonFullName) => {
            if (firstPersonFullName) {
              return firstPersonFullName;
            }

            if (resp.name) {
              return resp.name;
            }

            return resp.phoneNumber
              ? ContactModelV2.formatNumber(resp.phoneNumber)
              : undefined;
          });

          setAvatarElement(
            <img
              src={resp.DisplayAvatar || unknownCallerAvatar}
              className={'avatar'}
            />
          );

          setConfAvatarEls((prev) => [
            ...(prev || []),
            {
              uniqId:
                resp.name || ContactModelV2.formatNumber(resp.phoneNumber),
              data: popup,
            },
          ]);
        });
      });

      setNumberOfPeopleInConferenceRoom(peopleInARoom.length - 1);
    }
  }, [
    firstPersonFullName,
    phoneStore.conferenceRoomsIdAndNumbers,
    phoneStore.ActivePhoneCall.isMerge,
    phoneStore.selectPeopleOrContactsFromConferenceRoom,
    phoneStore.ConferenceIdGuid,
    phoneStore,
  ]);

  if (isEmpty(phoneStore.ActivePhoneCall)) {
    return <Loader active indeterminate size="mini" />;
  }

  const callIsInTransit =
    phoneStore.ActivePhoneCall?.isIncomingCall &&
    (phoneStore.ActivePhoneCall?.isMediaConnecting ||
      phoneStore.ActivePhoneCall?.isCallConnecting);

  return (
    <div
      className={cx('call-active', 'call-item', {
        extended: !phoneStore.ActivePhoneCall.isCallConnecting,
        connecting: phoneStore.ActivePhoneCall.isCallConnecting,
        'warm-transfer': phoneStore.ActivePhoneCall.isWarmTransfer,
        incoming: phoneStore.ActivePhoneCall.isIncomingCall,
      })}
    >
      {phoneStore.ActivePhoneCall.isMediaConnected && (
        <div className="call-stats">
          <div className="call-timer">
            {phoneStore.ActivePhoneCall.timerDisplay}
          </div>
          <div className="call-status">
            <CallConnectionStatus
              status={props.disabledActions ? 'off' : connectionQuality}
              callId={phoneStore.ActivePhoneCall.session?.id}
            />
          </div>
        </div>
      )}
      <div
        className={cx('call-active-first-row', {
          connecting: phoneStore.ActivePhoneCall.isCallConnecting,
        })}
      >
        {phoneStore.ActivePhoneCall.showDialPad && (
          <div className="calling-dialpad">
            <TopBarDialpad
              keyPadPush={keyPadPush}
              disabledActions={props.disabledActions}
            />
          </div>
        )}
        {!phoneStore.ActivePhoneCall.showDialPad && (
          <div
            className={cx('contact-image-wrapper', {
              isMerge:
                phoneStore.ActivePhoneCall.phoneStore.ActivePhoneCall.isMerge,
              disabledActions:
                props.disabledActions && props.isConnectionLostFeedbackVisible,
            })}
          >
            {phoneStore.ActivePhoneCall.phoneStore.ActivePhoneCall.isMerge && (
              <div className="small-avatars-list">
                {' '}
                {uniqBy(confAvatarEls, (ua) => ua.uniqId).map(
                  (a) => a.data
                )}{' '}
              </div>
            )}
            {!phoneStore.ActivePhoneCall.phoneStore.ActivePhoneCall.isMerge &&
              avatarElement}
          </div>
        )}
      </div>
      {
        <div className="call-content">
          {phoneStore.ActivePhoneCall.phoneStore.ActivePhoneCall.isMerge &&
            showPeopleInRoom && (
              <div className="call-details">
                <div className="header">
                  <div className="title">CALL DETAILS</div>
                  <div className="close" onClick={closeListOfPeopleInRoom}>
                    Close
                  </div>
                  <div className="push" />
                </div>
                <div className="call-details-list">
                  {uniqBy(listOfPeopleInARoom, (p) => p.uniqId).map((item) => {
                    return item.data;
                  })}
                </div>
              </div>
            )}
          <div
            className={cx('call-top', {
              disabledActions:
                props.disabledActions && props.isConnectionLostFeedbackVisible,
            })}
          >
            {phoneStore.ActivePhoneCall.phoneStore.ActivePhoneCall.isMerge &&
              phoneStore.ActivePhoneCall.isCallConnected && (
                <div data-private className="contact-info">
                  <>
                    <CallContactInfo
                      name={
                        phoneStore.ActivePhoneCall.contact?.name ||
                        firstPersonFullName
                      }
                    />
                    {numberOfPeopleInConferenceRoom > 0 && (
                      <div className={'contact-count'}>
                        & {numberOfPeopleInConferenceRoom}{' '}
                        {numberOfPeopleInConferenceRoom > 1
                          ? 'others'
                          : 'other'}
                      </div>
                    )}
                    <img
                      src={infoBtn}
                      className={'contact-info-btn'}
                      onClick={() => void displayListofPeopleInARoom()}
                    />
                  </>
                </div>
              )}
            <div data-private className="contact-info">
              {!(
                phoneStore.ActivePhoneCall.phoneStore.ActivePhoneCall.isMerge &&
                phoneStore.ActivePhoneCall.isCallConnected
              ) && (
                <>
                  {phoneStore.ActivePhoneCall.person !== null &&
                    phoneStore.ActivePhoneCall.person?.case({
                      fulfilled: (response) => {
                        return (
                          <CallContactInfo
                            name={`${response?.firstName} ${response?.lastName}`}
                            phoneNumber={
                              response?.lines && response.formatNumber
                                ? response?.formatNumber()
                                : ''
                            }
                          />
                        );
                      },
                    })}
                  {phoneStore.ActivePhoneCall.contact !== null ? (
                    (() => {
                      /**
                       * This is what I call a Mjölnir moment.
                       * When merging a call, the phone number comes with a big string containing the sip address.
                       * In this specific case, we don't want it to be displayed.
                       */
                      const phoneNumber =
                        phoneStore.ActivePhoneCall.phoneNumber.includes('sip')
                          ? undefined
                          : ContactModelV2.formatNumber(
                              phoneStore.ActivePhoneCall.phoneNumber
                            );

                      return (
                        <CallContactInfo
                          name={phoneStore.ActivePhoneCall.contact.name}
                          others={phoneStore.ActivePhoneCall.otherContacts}
                          phoneNumber={phoneNumber}
                        />
                      );
                    })()
                  ) : phoneStore.ActivePhoneCall.callerIdName &&
                    !phoneStore.ActivePhoneCall.person ? (
                    <CallContactInfo
                      name={phoneStore.ActivePhoneCall.callerIdName}
                      phoneNumber={
                        isPhoneNumberMatch(
                          phoneStore.ActivePhoneCall.callerIdName,
                          phoneStore.ActivePhoneCall.phoneNumber
                        )
                          ? undefined
                          : formatNumberWithNationalCode(
                              phoneStore.ActivePhoneCall.phoneNumber
                            )
                      }
                    />
                  ) : (
                    <CallContactInfo
                      name={phoneStore.ActivePhoneCall?.FormattedNumber}
                    />
                  )}
                </>
              )}
              {phoneStore.ActivePhoneCall &&
                phoneStore.ActivePhoneCall.isIncomingCall &&
                callIsInTransit && (
                  <div className="transit-call-status">
                    <span>
                      {phoneStore.ActivePhoneCall?.isCallConnecting
                        ? 'incoming call..'
                        : 'connecting...'}
                    </span>
                  </div>
                )}
              {!phoneStore.ActivePhoneCall?.isIncomingCall &&
                !phoneStore.ActivePhoneCall?.isCallConnected && (
                  <div className="transit-call-status">
                    <span>
                      {phoneStore.ActivePhoneCall?.isCallConnecting
                        ? 'calling...'
                        : 'connecting...'}
                    </span>
                  </div>
                )}
            </div>
          </div>
          <div className="btn-wrapper">
            {phoneStore.ActivePhoneCall &&
              !phoneStore.ActivePhoneCall.isCallConnected && (
                <CallButtons
                  inbound={phoneStore.ActivePhoneCall?.isIncomingCall}
                  lineActions={{
                    pickup,
                    reject,
                    cancel,
                  }}
                  isWebRTCConnected={phoneStore.isWebRTCConnected}
                />
              )}
            {phoneStore.ActivePhoneCall &&
              phoneStore.ActivePhoneCall?.isCallConnected &&
              phoneStore.ActivePhoneCall?.isMediaConnected && (
                <CallActiveButtons
                  disabledActions={props.disabledActions}
                  loggedInAccountId={props.loggedInAccountId}
                  phoneCallModel={phoneStore.ActivePhoneCall}
                  transferToConference={phoneStore.transferToConference}
                  muteCall={muteCall}
                  toggleDialPad={phoneStore.ActivePhoneCall.toggleDialPad}
                  holdCall={holdCall}
                  transferCall={transferCall}
                  endCall={endCall}
                  loadOrCreateConversationWithPost={
                    props.loadOrCreateConversationWithPost
                  }
                  postConferenceByConversationId={
                    props.postConferenceByConversationId
                  }
                  oldSession={phoneStore.warmTransferOld}
                  loggedInUserActiveConferenceConversation={
                    props.loggedInUserActiveConferenceConversation
                  }
                  ConferenceIdGuid={phoneStore.ConferenceIdGuid}
                  conferenceRoomsIdAndNumbers={
                    phoneStore.conferenceRoomsIdAndNumbers
                  }
                />
              )}
          </div>
        </div>
      }

      {renderKeypadAudioElements()}
    </div>
  );
};

export default observer(CallActive);
